import { NavLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { LucideIcon } from 'lucide-react';
import { ROUTES } from '@/shared/constants/routes';
import { Icons } from '@/shared/constants/icons';
import { useInboxBadges } from '@/shared/hooks/useInboxBadges';
import { useAttentionCount } from '@/shared/hooks/useAttention';
import { openItems, useCurrentPlan } from '@/shared/hooks/usePlan';
import { useCanWrite } from '@/shared/hooks/useCanWrite';
import { canReadAnalytics, canReadInbox } from '@/shared/lib/role-access';

interface MobileNavProps {
  /** Opens the sidebar drawer, which holds everything not on the bar. */
  onMore: () => void;
}

interface MobileNavItem {
  path: string;
  icon: LucideIcon;
  labelKey: string;
}

/** Home (DS04): phones had no way home but the More drawer. */
const HOME_ITEM: MobileNavItem = { path: ROUTES.OVERVIEW, icon: Icons.home, labelKey: 'nav.overview' };
const LEFT: MobileNavItem[] = [HOME_ITEM, { path: ROUTES.INBOX, icon: Icons.inbox, labelKey: 'nav.inbox' }];
// The weekly plan stands for Publish here (five slots); Calendar and the rest are in More (Task 2.3).
const PLAN_ITEM: MobileNavItem = { path: ROUTES.PLAN, icon: Icons.plan, labelKey: 'nav.plan' };
/** Without the planner (Starter, or switched off) the bar keeps Calendar, not a locked Plan (PC-52b). */
const CALENDAR_ITEM: MobileNavItem = { path: ROUTES.CALENDAR, icon: Icons.calendar, labelKey: 'nav.calendar' };
/** A client's bar: what waits for them, the calendar, the results (PC-26). */
const CLIENT_LEFT: MobileNavItem[] = [
  { path: ROUTES.APPROVALS, icon: Icons.check, labelKey: 'nav.approvals' },
  CALENDAR_ITEM,
];
const CLIENT_RIGHT: MobileNavItem[] = [{ path: ROUTES.ANALYTICS, icon: Icons.analytics, labelKey: 'nav.analytics' }];

const itemClass = (active: boolean): string =>
  [
    'relative flex flex-col items-center justify-center gap-[3px] min-h-[44px] min-w-[44px] px-2 rounded-[10px] no-underline flex-1 max-w-[76px] bg-transparent border-none cursor-pointer',
    active ? 'text-[var(--color-accent)]' : 'text-[var(--ink-2)]',
  ].join(' ');

/** Count pill on a tab icon. Hidden from AT; the tab's sr-only text carries the number. */
function TabCount({ count, tone }: { count: number; tone: 'accent' | 'warn' }): React.ReactElement | null {
  if (count <= 0) return null;
  return (
    <span
      className="absolute -top-1.5 -end-2.5 text-[10px] font-semibold px-[5px] rounded-full leading-[1.5] tabular-nums"
      style={tone === 'accent' ? { background: 'var(--color-accent)', color: '#fff' } : { background: 'var(--warn-soft)', color: 'var(--color-warning-text)' }}
      aria-hidden="true"
    >
      {count > 99 ? '99+' : count}
    </span>
  );
}

/**
 * Phone bottom bar (blueprint S90, DS04): Home, Inbox, Compose (centre,
 * raised), Next week (Calendar without the planner), More. The badges mirror
 * the rail: unread count and a red dot for anything escalated to me on Inbox,
 * the open attention count on Home. All targets
 * are at least 44px and the bar clears the home indicator.
 */
export function MobileNav({ onMore }: MobileNavProps): React.ReactElement {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { unread, escalated } = useInboxBadges();
  const attentionCount = useAttentionCount();
  const { data: planData } = useCurrentPlan();
  const planCount = openItems(planData?.plan).length;
  const { canWrite, role } = useCanWrite();
  const client = role === 'client';
  // A role never sees a destination the API refuses it (AL-06).
  const allowedFor = (item: MobileNavItem): boolean => (item.path !== ROUTES.INBOX || canReadInbox(role)) && (item.path !== ROUTES.ANALYTICS || canReadAnalytics(role));
  const left = (client ? CLIENT_LEFT : LEFT).filter(allowedFor);
  const right = (client ? CLIENT_RIGHT : [planData && planData.enabled === false ? CALENDAR_ITEM : PLAN_ITEM]).filter(allowedFor);

  const renderLink = (item: MobileNavItem): React.ReactElement => {
    const Icon = item.icon;
    const isInbox = item.path === ROUTES.INBOX;
    // Home holds what needs attention, so its tab carries that count.
    const isAttention = item.path === ROUTES.OVERVIEW;
    const isPlan = item.path === ROUTES.PLAN;
    return (
      <NavLink key={item.path} to={item.path} className={({ isActive }) => itemClass(isActive)}>
        {({ isActive }) => (
          <>
            <span className="relative inline-flex">
              <Icon size={24} strokeWidth={isActive ? 2 : 1.5} aria-hidden="true" />
              {isInbox && <TabCount count={unread} tone="accent" />}
              {isInbox && escalated > 0 && (
                <span data-testid="escalated-dot" className="absolute -top-0.5 -start-0.5 w-2 h-2 rounded-full bg-[var(--bad)] ring-2 ring-[var(--color-surface)]" aria-hidden="true" />
              )}
              {isAttention && <TabCount count={attentionCount} tone="warn" />}
              {isPlan && <TabCount count={planCount} tone="accent" />}
            </span>
            <span className="text-caption font-semibold">{t(item.labelKey)}</span>
            {isInbox && unread > 0 && <span className="sr-only">{t('nav.unreadCount', { count: unread })}</span>}
            {isInbox && escalated > 0 && <span className="sr-only">{t('nav.escalatedCount', { count: escalated })}</span>}
            {isAttention && attentionCount > 0 && <span className="sr-only">{t('nav.attentionCount', { count: attentionCount })}</span>}
          </>
        )}
      </NavLink>
    );
  };

  return (
    <nav
      className="lg:hidden fixed bottom-0 inset-x-0 z-[45] border-t border-[var(--line)] flex items-center justify-around"
      style={{
        background: 'var(--color-surface)',
        height: 'calc(64px + env(safe-area-inset-bottom, 0px))',
        padding: '0 4px',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
      }}
      aria-label={t('nav.mobile')}
    >
      {left.map(renderLink)}

      {canWrite && <button
        type="button"
        onClick={() => navigate(ROUTES.COMPOSER)}
        className="flex items-center justify-center -mt-6 rounded-full text-white shrink-0 cursor-pointer"
        style={{ width: 52, height: 52, background: 'var(--color-accent)', border: '4px solid var(--color-bg)', boxShadow: 'var(--shadow-md)' }}
        aria-label={t('nav.compose')}
      >
        <Icons.add size={24} aria-hidden="true" />
      </button>}

      {right.map(renderLink)}

      <button type="button" onClick={onMore} className={itemClass(false)} aria-label={t('nav.more')}>
        <Icons.more size={24} strokeWidth={1.5} aria-hidden="true" />
        <span className="text-caption font-semibold">{t('nav.more')}</span>
      </button>
    </nav>
  );
}
